import { Component, Input, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import * as d3 from 'd3';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IterlexService } from '../iterlex.service';
import { ItModalComponent, ItCheckboxComponent } from 'design-angular-kit';
import type { Authority } from '../interfaces/authority';
import type { Dossier } from '../interfaces/dossier';
import type { Phase } from '../interfaces/phase';
import { HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

interface NodeData {
	name: string;
	type?: string;
	id?: number | string;
	hover?: string;
	children?: NodeData[];
}
interface HierarchyNode extends d3.HierarchyNode<NodeData> {
	x0: number;
	y0: number;
}

@Component({
	selector: 'app-dossier-graph',
	imports: [
		FormsModule,
		ItModalComponent,
		ItCheckboxComponent,
		TranslateModule,
	],
	templateUrl: './dossier-graph.component.html',
	styleUrl: './dossier-graph.component.scss',
})
export class DossierGraphComponent {
	private router = inject(Router);
	private translateService = inject(TranslateService);
	private iterlexService = inject(IterlexService);

	@ViewChild(ItModalComponent) graphModal!: ItModalComponent;
	@Input({ required: true }) dossier!: Dossier;
	@Input({ required: true }) authority!: Authority;
	@Input({ required: true }) body!: Authority;
	@Input({ required: true }) phases!: Phase[];
	showOnlyLatestExpression = true;
	data: NodeData = {
		name: '',
		type: 'dossier',
		children: [
			{
				name: '',
				type: 'phase',
				id: 0,
				children: [
					{
						name: '',
						type: 'step',
						id: 0,
						children: [
							{
								name: '',
								type: 'document',
								children: [
									{
										name: 'version',
										children: [{ name: 'file' }],
									},
								],
							},
						],
					},
				],
			},
		],
	};

	width = 1000;
	marginTop = 10;
	marginRight = 10;
	marginBottom = 10;
	marginLeft = 10;

	// Rows are separated by dx pixels, columns by dy pixels.
	// These names can be counter-intuitive (dx is a height, and dy a width).
	// This because the tree must be viewed with the root at the "bottom", in the data domain. The width of a column is based on the tree's height.
	root = d3.hierarchy(this.data);
	dx = 10;
	dy =
		(this.width - this.marginRight - this.marginLeft) /
		(1 + this.root.height);
	tree = d3.tree<NodeData>().nodeSize([this.dx, this.dy]);

	// Define the tree layout and the shape for links
	diagonal = d3
		.linkHorizontal()
		.x((d: any) => d.y)
		.y((d: any) => d.x);

	svg!: d3.Selection<SVGSVGElement, undefined, null, undefined>;
	gLink!: d3.Selection<SVGGElement, undefined, null, undefined>;
	gNode!: d3.Selection<SVGGElement, undefined, null, undefined>;

	async createSvg() {
		// Create the SVG container, a layer for the links and a layer for the nodes
		this.svg = d3
			.create('svg')
			.attr('width', '100%')
			.attr('height', this.dx)
			.attr('viewBox', [
				-this.marginLeft,
				-this.marginTop,
				this.width,
				this.dx,
			])
			.attr(
				'style',
				'max-width: 100%; height: auto; font: 10px sans-serif; user-select: none;',
			);

		this.gLink = this.svg
			.append('g')
			.attr('fill', 'none')
			.attr('stroke', 'var(--bs-secondary)')
			.attr('stroke-opacity', 0.4)
			.attr('stroke-width', 1.5);

		this.gNode = this.svg
			.append('g')
			.attr('cursor', 'pointer')
			.attr('pointer-events', 'all');

		const language =
			this.translateService.getCurrentLang() ??
			this.translateService.getFallbackLang();

		this.data = {
			name: this.dossier.title,
			type: 'dossier',
			children: await Promise.all(
				this.phases.map(async (phase) => ({
					name: phase.label[language],
					type: 'phase',
					phaseName: phase.id,
					id: phase.id,
					children: await Promise.all(
						(
							await firstValueFrom(
								this.iterlexService.getSteps(
									this.authority.id,
									this.body.id,
									this.dossier.workUri,
									phase.id,
								),
							)
						).map(async (step) => ({
							name: step.label[language],
							phaseName: phase.id,
							stepId: step.id,
							id: step.id,
							type: 'step',
							children: await Promise.all(
								(
									await firstValueFrom(
										this.iterlexService.getDocuments(
											new HttpParams()
												.append(
													'dossierUri',
													this.dossier.workUri,
												)
												.append(
													'authority',
													this.authority.id,
												)
												.append('body', this.body.id)
												.append('phaseName', phase.id)
												.append('stepId', step.id),
										),
									)
								).value.map(async (document) => ({
									name: document.title,
									id: document.workUri,
									phaseName: phase.id,
									stepId: step.id,
									type: 'document',
									children: ((document) => {
										if (
											this.showOnlyLatestExpression &&
											document.versions.length > 1
										) {
											document.versions = [
												document.versions[
													document.versions.length - 1
												],
											];
										}
										return document.versions.map(
											(version) => ({
												name:
													new DatePipe(
														'en-US',
													).transform(
														version.date,
														'd/M/yyyy',
													) ?? version.date,
												phaseName: phase.id,
												stepId: step.id,
												children: version.files.map(
													(file) => ({
														name: `[${file.title.split('.').at(-1)?.toUpperCase()}] ${file.title}`,
														hover: file.title,
														phaseName: phase.id,
														stepId: step.id,
													}),
												),
											}),
										);
									})(
										await firstValueFrom(
											this.iterlexService.getDocumentComplete(
												this.authority.id,
												this.body.id,
												document.workUri,
												phase.id,
												step.id,
											),
										),
									),
								})),
							),
						})),
					),
				})),
			),
		};
		this.root = d3.hierarchy(this.data);
		(this.root as any).x0 = this.dy / 2;
		(this.root as any).y0 = 0;
		this.root.descendants().forEach((d: any, i) => {
			d.id = i;
			d._children = d.children;
		});
		this.update(this.root as HierarchyNode);
		document.getElementById('tree')?.replaceChildren(this.svg.node()!);
	}
	update(source: HierarchyNode) {
		// Compute the new tree layout.
		const tree = this.tree(this.root);

		const nodes = tree.descendants().reverse();

		const links = tree.links();
		let left = tree;
		let right = tree;
		tree.eachBefore((node) => {
			if (node.x < left.x) left = node;
			if (node.x > right.x) right = node;
		});

		const height = right.x - left.x + this.marginTop + this.marginBottom;

		const transition = this.svg
			.transition()
			.duration(250)
			.attr('height', height)
			.attr('viewBox', [
				-this.marginLeft,
				left.x - this.marginTop,
				this.width,
				height,
			] as any);

		// Update the nodes
		const node = this.gNode.selectAll('g').data(nodes, (d: any) => d.id);

		// Enter any new nodes at the parent's previous position
		const nodeEnter = node
			.enter()
			.append('g')
			.attr('transform', () => `translate(${source.y0},${source.x0})`)
			.attr('fill-opacity', 0)
			.attr('stroke-opacity', 0)
			.on('click', (_, d: any) => {
				if (d._children) {
					d.children = d.children ? null : d._children;
					this.update(d);
				} else {
					this.router
						.navigate([], {
							queryParams: {
								phase: d.data.phaseName,
								step: d.data.stepId,
							},
							queryParamsHandling: 'merge',
						})
						.then(() => window.location.reload());
				}
			});

		// Dot of nodes
		nodeEnter
			.append('circle')
			.attr('r', 2.5)
			.attr('fill', 'var(--bs-primary)')
			.attr('stroke-width', 10);

		// Text of nodes
		nodeEnter
			.append('text')
			.attr('dy', '0.31em')
			.attr('x', 6)
			.attr('text-anchor', 'start')
			.text((d) => d.data.name)
			.attr('stroke-linejoin', 'round')
			.attr('stroke-width', 3)
			.attr('stroke', 'white')
			.attr('fill', (d: any) => (!d._children ? 'var(--bs-danger)' : ''))
			.attr('paint-order', 'stroke');

		nodeEnter.append('title').text((d) => d.data.hover ?? '');

		// Transition nodes to their new position
		node.merge(nodeEnter as any)
			.transition(transition as any)
			.attr('transform', (d) => `translate(${d.y},${d.x})`)
			.attr('fill-opacity', 1)
			.attr('stroke-opacity', 1);

		// Transition exiting nodes to the parent's new position
		node.exit()
			.transition(transition as any)
			.remove()
			.attr('transform', () => `translate(${source.y},${source.x})`)
			.attr('fill-opacity', 0)
			.attr('stroke-opacity', 0);

		// Update the links
		const link = this.gLink
			.selectAll('path')
			.data(links, (d: any) => d.target.id);

		// Enter any new links at the parent's previous position
		const linkEnter = link
			.enter()
			.append('path')
			.attr('d', () => {
				const o = { x: source.x0, y: source.y0 };
				return this.diagonal({ source: o, target: o } as any);
			});

		// Transition links to their new position
		link.merge(linkEnter as any)
			.transition(transition as any)
			.attr('d', this.diagonal as any);

		// Transition exiting nodes to the parent's new position
		link.exit()
			.transition(transition as any)
			.remove()
			.attr('d', () => {
				const o = { x: source.x, y: source.y };
				return this.diagonal({ source: o, target: o } as any);
			});

		// Stash the old positions for transition
		this.root.eachBefore((d: any) => {
			d.x0 = d.x;
			d.y0 = d.y;
		});
	}
	public toggle() {
		this.graphModal.toggle();
	}
}
